'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LogOut, ChevronDown, Wifi, WifiOff, RefreshCw, BedDouble, Users, Shield, Settings, BarChart2, Layers } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

function formatSyncTime(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return date.toLocaleDateString();
}

function SyncBadge() {
  const [isOnline, setIsOnline] = useState(true);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [displayTime, setDisplayTime] = useState('Never');

  useEffect(() => {
    setIsOnline(navigator.onLine);
    const stored = localStorage.getItem('anc_last_sync');
    if (stored) {
      const d = new Date(stored);
      setLastSync(d);
      setDisplayTime(formatSyncTime(d));
    }

    const handleOnline = () => {
      setIsOnline(true);
      triggerSync();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayTime(formatSyncTime(lastSync));
    }, 60000);
    return () => clearInterval(interval);
  }, [lastSync]);

  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(() => {
      triggerSync();
    }, 5 * 60 * 1000);
    triggerSync();
    return () => clearInterval(interval);
  }, [isOnline]);

  function triggerSync() {
    if (!navigator.onLine) return;
    setSyncing(true);
    setTimeout(() => {
      const now = new Date();
      setLastSync(now);
      setDisplayTime(formatSyncTime(now));
      localStorage.setItem('anc_last_sync', now.toISOString());
      setSyncing(false);
    }, 800);
  }

  return (
    <button
      onClick={triggerSync}
      disabled={syncing || !isOnline}
      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/60 active:bg-muted transition-colors flex-shrink-0"
      title={isOnline ? `Online · Last saved ${displayTime}` : 'Offline · Changes will sync when connected'}
    >
      {syncing ? (
        <RefreshCw size={12} className="text-primary animate-spin" />
      ) : isOnline ? (
        <Wifi size={12} className="text-green-500" />
      ) : (
        <WifiOff size={12} className="text-red-400" />
      )}
      <div className="flex flex-col items-start leading-none">
        <span className={`text-[9px] font-semibold ${isOnline ? 'text-green-600' : 'text-red-500'}`}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
        <span className="text-[9px] text-muted-foreground">
          {syncing ? 'Saving…' : displayTime}
        </span>
      </div>
    </button>
  );
}

export default function AppHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, signOut, isAdmin } = useAuth();
  const router = useRouter();

  const fullName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const farmRole = user?.user_metadata?.farm_role || 'Farm Worker';
  const initials = fullName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await signOut();
      window.location.href = '/sign-up-login-screen';
    } catch (err) {
      window.location.href = '/sign-up-login-screen';
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border flex items-center px-3 gap-2 shadow-sm">
      <Link href="/" className="flex items-center gap-2 flex-shrink-0">
        <AppLogo size={28} />
        <span className="font-bold text-base text-primary tracking-tight">
          ANC@JFAgro
        </span>
      </Link>
      <div className="flex-1" />
      <SyncBadge />
      <div className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 active:bg-muted transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="flex flex-col items-start">
            <span className="text-xs font-semibold text-foreground leading-none">
              {fullName}
            </span>
            <span className="text-[10px] text-muted-foreground leading-none mt-0.5">
              {farmRole}
            </span>
          </div>
          <ChevronDown size={13} className="text-muted-foreground" />
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-2 w-44 bg-card border border-border rounded-xl shadow-lg z-50 py-1">
            <div className="px-4 py-2 border-b border-border">
              <p className="text-sm font-semibold text-foreground">{fullName}</p>
              <p className="text-xs text-muted-foreground">{farmRole}</p>
            </div>
            {isAdmin && (
              <Link
                href="/admin-panel"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-primary font-semibold active:bg-muted transition-colors"
              >
                <Shield size={15} className="text-primary" />
                Admin Panel
              </Link>
            )}
            {isAdmin && (
              <Link
                href="/admin-analytics"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-primary font-semibold active:bg-muted transition-colors"
              >
                <BarChart2 size={15} className="text-primary" />
                Analytics
              </Link>
            )}
            {isAdmin && (
              <Link
                href="/admin-management"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-primary font-semibold active:bg-muted transition-colors"
              >
                <Settings size={15} className="text-primary" />
                Admin Management
              </Link>
            )}
            <Link
              href="/worm-beds"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground active:bg-muted transition-colors"
            >
              <BedDouble size={15} className="text-muted-foreground" />
              Worm Beds
            </Link>
            <Link
              href="/anc-system"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground active:bg-muted transition-colors"
            >
              <Layers size={15} className="text-muted-foreground" />
              ANC System
            </Link>
            <Link
              href="/farm-workers"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground active:bg-muted transition-colors"
            >
              <Users size={15} className="text-muted-foreground" />
              Farm Workers
            </Link>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-danger active:bg-danger-bg transition-colors"
            >
              <LogOut size={15} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}